/* eslint-disable @typescript-eslint/no-unsafe-function-type */

// base
import { useMemo, useCallback, useRef, useState } from 'react';

const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 20;

export interface UsePaginationProps {
  totalElement: number;
  defaultPage?: number;
  defaultPageSize?: number;
  onChangePagination?: (page: number, size: number) => void;
}

export interface Pagination {
  total: number;
  current: number;
  pageSize: number;
  showSizeChanger: boolean;
  onChange: (page: number, pageSize?: number) => void;
}

export interface UsePaginationReturn {
  page: number;
  pageSize: number;
  pagination: Pagination;
  onChangePageSize: (page: number, pageSize?: number) => void;
  setPagination: (page?: number, pageSize?: number) => void;
}

export const usePaginationV2 = ({
  totalElement,
  onChangePagination,
  defaultPage = DEFAULT_PAGE,
  defaultPageSize = DEFAULT_PAGE_SIZE
}: UsePaginationProps): UsePaginationReturn => {
  const [pager, setPager] = useState({
    page: defaultPage,
    pageSize: defaultPageSize
  });

  const changePaginationRef = useRef<Function>();

  changePaginationRef.current = onChangePagination;

  const setPagination = useCallback((page?: number, pageSize?: number) => {
    setPager((pager) => Object.assign({}, pager, { page, pageSize }));
  }, []);

  const pagination = useMemo(() => {
    return {
      total: totalElement,
      current: pager.page,
      pageSize: pager.pageSize,
      showSizeChanger: false,
      onChange: (page: number, pageSize: number = pager.pageSize) => {
        if (changePaginationRef.current) {
          changePaginationRef.current(page, pageSize);
        }

        setPagination(page, pageSize);
      }
    };
  }, [totalElement, pager, setPagination]);

  const onChangePageSize = useCallback(
    (page: number, pageSize: number = pager.pageSize) => {
      if (changePaginationRef.current) {
        changePaginationRef.current(page, pageSize);
      }

      setPagination(page, pageSize);
    },
    [pager.pageSize, setPagination]
  );

  return {
    page: pager.page,
    pageSize: pager.pageSize,
    pagination,
    onChangePageSize,
    setPagination
  };
};
